import { adobeDataLayerTrack } from './analytics';
import { isLoggedInUser, signout } from './auth';
import { clone } from '../../utils/clone';
import { csrf } from './csrf';
import { keys as headerKeys, values as headerValues } from './headers';
import { log } from './log';
import { JWT, Organization, Profile, ProfileAttributes, Ready, SolutionLevel, SolutionRole } from './keys';
import { render } from './page';
import { override } from './regex';
import { request } from './request';
import { profile as profileUrl, track as trackUrl, vote as voteUrl, profileNew as profileNewUrl } from './urls';
import { relicApiFail, relicApiSuccess } from '../app/utils/relic';

const RELIC_SUPPORT_PAGE_AVAILABILITY_TRACKING = {
  title: 'Support Page Availability',
  duration: 10,
};

const classes = ['bookmark', 'bookmarked'];

let profileData = null;
let meta = {};

async function profileAttributes() {
  if (ProfileAttributes in sessionStorage === false) {
    const profileAttributesStart = performance.now();
    const profileAttributesDuration = Number((performance.now() - profileAttributesStart) );
    try {
      const res = await request(profileNewUrl, {
        credentials: 'include',
        headers: {
          [headerKeys.auth]: sessionStorage.getItem(JWT),
          [headerKeys.accept]: headerValues.json,
        },
      });

      if (res.ok) {
        const data = await res.json();
        sessionStorage.setItem(ProfileAttributes, JSON.stringify(data.data));
        relicApiSuccess({
          endpoint: profileNewUrl,
          duration: profileAttributesDuration,
          ...RELIC_SUPPORT_PAGE_AVAILABILITY_TRACKING,
        });
      } else {
        relicApiFail({
          status:res.status,
          endpoint: profileNewUrl,
          duration: profileAttributesDuration,
          ...RELIC_SUPPORT_PAGE_AVAILABILITY_TRACKING,
        });
      }
    } catch (err) {
      log(`Profile attributes request failed: ${err?.message || err}`, { type: 'error' });
      relicApiFail({
        status:err.status,
        endpoint: profileNewUrl,
        duration: profileAttributesDuration,
        ...RELIC_SUPPORT_PAGE_AVAILABILITY_TRACKING,
      });
    }
  }

  return JSON.parse(sessionStorage.getItem(ProfileAttributes) || '{}');
}

async function profileMerge(arg) {
  const tmp = await window.adobeIMS.getProfile();

  console.log('arg profileMerge', arg);
  
  return { ...tmp, ...arg };
}

/** IMS + session cache only; skips the Experience League profile API. */
export async function profileLightweight() {
  const data = await window.adobeIMS.getProfile();
  if (data === null) {
    return null;
  }

  const cached = sessionStorage.getItem(Profile);
  if (cached) {
    try {
      const parsed = JSON.parse(cached);
      if (parsed?.email || parsed?.userId) {
        return parsed;
      }
    } catch {
      // use IMS fallback below
    }
  }

  return {
    ...data,
    first_name: data.firstName ?? data.first_name,
    last_name: data.lastName ?? data.last_name,
    orgs: data.orgs ?? [],
    interactions: [],
  };
}

export async function profile(reuse = false, cstream = true, explicit = false) {
  let result = null;
  const authenticated = await isLoggedInUser();

  if (reuse === false) {
    if (authenticated) {
      const data = await window.adobeIMS.getProfile();
      const profileAttributesStart = performance.now();
      const profileAttributesDuration = Number((performance.now() - profileAttributesStart) );

      if (data !== null) {

        if (profileData === null || explicit) {
          log('Retrieving Experience League profile');
          let res = null;
          let profileData = await adobeIMS.getProfile();
          try {
            res = await request(profileNewUrl, {
              credentials: 'include',
              headers: {
                Authorization : `Bearer ${window.adobeIMS.getAccessToken()?.token || ''}`,
                [headerKeys.accept]: headerValues.json,
              },
              params:{
                auth_id: profileData.userId,
                user_id: profileData.userId
              }
            });
          } catch (err) {
            log(`Experience League profile request failed: ${err?.message || err}`, { type: 'error' });
            relicApiFail({
              status:err.status,
              endpoint: profileNewUrl,
              duration: profileAttributesDuration,
              ...RELIC_SUPPORT_PAGE_AVAILABILITY_TRACKING,
            });
            newrelic.addPageAction('PAGE_LOADED', {
              orgPickerRendered: false,
              eccOrgsFetched: false,
              commerceOrgsFetched: false,
              workfrontOrgsRendered: false,
              profileAPIFetched: false,
              pageLoaded : false,
              allChecksPass: false,
              auth_id: profileData.userId,
              user_id: profileData.userId,
              errorMessage: err?.message || String(err || 'Profile API fetch failed'),
            });
            if (profileData !== null) {
              result = clone(profileData);
            } else {
              try {
                result = await profileMerge({});
              } catch (e) {
                console.error(`profile fallback after fetch failure:`, e);
              }
            }
          }

          const arg = await res.json(); 

          console.log('arg profile', arg);
          if (arg !== null) {
            if (arg.success) {
              try {
                arg.organizations.sort((a, b) =>
                  a.orgName.localeCompare(b.orgName)
                );
                result = await profileMerge({orgs:arg.organizations});
                console.log('profileMerge result', result);
                result.interactions = []
                profileData = clone(result);

                relicApiSuccess({
                  endpoint: profileNewUrl,
                  duration: profileAttributesDuration,
                  ...RELIC_SUPPORT_PAGE_AVAILABILITY_TRACKING,
                });

                if (cstream === undefined) {
                  console.log('no stream');
                }
              } catch (e) {
                relicApiFail({
                  status:e.status,
                  endpoint: profileNewUrl,
                  duration: profileAttributesDuration,
                  ...RELIC_SUPPORT_PAGE_AVAILABILITY_TRACKING,
                });
                newrelic.addPageAction('PAGE_LOADED', {
                  orgPickerRendered: false,
                  eccOrgsFetched: false,
                  commerceOrgsFetched: false,
                  workfrontOrgsRendered: false,
                  profileAPIFetched: false,
                  pageLoaded : false,
                  allChecksPass: false,
                  auth_id: profileData.userId,
                  user_id: profileData.userId,
                  errorMessage: e?.message || String(e || 'Profile API fetch failed'),
                });
              }
            } else {
              relicApiFail({
                status:arg.status,
                endpoint: profileNewUrl,
                duration: profileAttributesDuration,
                ...RELIC_SUPPORT_PAGE_AVAILABILITY_TRACKING,
              });
              newrelic.addPageAction('PAGE_LOADED', {
                orgPickerRendered: false,
                eccOrgsFetched: false,
                commerceOrgsFetched: false,
                workfrontOrgsRendered: false,
                profileAPIFetched: false,
                pageLoaded : false,
                allChecksPass: false,
                auth_id: profileData.userId,
                user_id: profileData.userId,
                errorMessage: arg?.message || arg?.error || `Profile API failed (${arg?.status || ''})`,
              });
              signout();
            }
          }
        } else {
          result = clone(profileData);
        }
      } else {
        if (typeof newrelic !== 'undefined' && typeof newrelic.addPageAction === 'function') {
          newrelic.addPageAction('PAGE_LOADED', {
            orgPickerRendered: false,
            eccOrgsFetched: false,
            commerceOrgsFetched: false,
            workfrontOrgsRendered: false,
            profileAPIFetched: false,
            pageLoaded: false,
            allChecksPass: false,
            errorMessage: 'IMS profile returned null',
          });
        }
        signout();
      }
    } else {
      signout();
    }
  } else {
    result = clone(profileData);
  }

  if (result !== null) {
    if (Reflect.ownKeys(meta).length === 0) {
      meta = await profileAttributes();
    }

    const keys = ['industryInterests', 'role'];
    const complete = Math.ceil(
      (keys.filter((k) => (result[k] || []).length > 0).length / keys.length) * 100,
    );

    sessionStorage.setItem(Ready, complete === 100);
    sessionStorage.setItem(Organization, result.org || '');
    sessionStorage.setItem(Profile, JSON.stringify(result));
    localStorage.setItem(SolutionLevel, (result.level || []).join(','));
    localStorage.setItem(SolutionRole, (result.role || []).join(','));
  }

  return result;
}

export async function updateProfile(key, val, replace = false) {
  const data = await profile(false, false, true);

  if (data == null) {
    log('updateProfile: no profile data; skipping update', { type: 'error' });
    return profileData;
  }

  if (Reflect.ownKeys(meta).length === 0) {
    meta = await profileAttributes();
  }

  Reflect.ownKeys(data).forEach((i) => {
    if (meta?.write?.includes(i) === false) {
      delete data[i];
    }
  });

  if (override.test(key) || replace === true) {
    data[key] = val;
  } else if (meta.types[key] === 'array') {
    if (data[key] === void 0 || replace === true) {
      data[key] = [val];
    } else if (Array.isArray(data[key]) === false) {
      data[key] = [data[key], val];
    } else {
      (Array.isArray(val) ? val : [val]).forEach((arg) => {
        if (data[key].includes(arg) === false) {
          data[key].push(arg);
        } else {
          data[key].splice(data[key].indexOf(arg), 1);
        }
      });
    }
  } else {
    data[key] = val;
  }

  let res;
  try {
    res = await request(profileNewUrl, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        authorization: sessionStorage.getItem(JWT),
        accept: 'application/json',
        'content-type': 'application/json-patch+json',
        'x-csrf-token': await csrf(),
      },
      body: JSON.stringify([{ op: 'replace', path: `/${key}`, value: data[key] }]),
    });
  } catch (err) {
    log(`updateProfile PATCH failed: ${err?.message || err}`, { type: 'error' });
    return profileData;
  }

  if (res.ok && res.status < 400) {
    const arg = await res.json();

    profileData = await profileMerge(arg.data);
    await profile(true);
    sessionStorage.setItem(Profile, JSON.stringify(profileData));
  }

  return profileData;
}

export async function bookmark(el, arg) {
  if (authenticated) {
    await updateProfile('bookmarks', arg);

    render(() => {
      const icon = el.querySelector('.icon');

      for (const klass of classes) {
        icon.classList.toggle(klass);
      }
    });
  }

  return arg;
}

export async function report(arg) {
  if (authenticated) {
    try {
      const res = await request(trackUrl, {
        credentials: 'include',
        headers: {
          [headerKeys.auth]: sessionStorage.getItem(JWT),
          [headerKeys.accept]: headerValues.json,
          [headerKeys.ctype]: headerValues.json,
          [headerKeys.csrf]: await csrf(),
        },
        method: 'POST',
        body: JSON.stringify(arg),
      });

      if (res.ok && profileData !== null && profileData.seen.includes(arg.id) === false) {
        profileData.seen.push(arg.id); // Keeping cache in sync with the server
      }
    } catch (err) {
      log(err.message, 'error');
    }
  }

  return arg;
}

export function seen(arg) {
  if (authenticated) {
    let obj = arg;

    while (
      obj.classList.contains('media') === false &&
      obj.classList.contains('card-content') === false &&
      obj.classList.contains('column') === false &&
      obj.parentNode !== obj &&
      obj.classList.contains('result') === false &&
      obj.classList.contains('actions')
    ) {
      obj = obj.parentNode;
    }

    const icon = obj.querySelector('.icon.seen.is-hidden');

    if (icon !== null) {
      render(() => icon.classList.remove('is-hidden'));
    }
  }
}

export async function vote(el, id, comment = '') {
  if (authenticated) {
    const data = await profile(true) || {};
    const { direction } = el.dataset;
    const otherDirection = direction === 'up' ? 'down' : 'up';
    const target = (data?.votes || {})[direction] || [];
    const other = (data?.votes || {})[otherDirection] || [];
    const icon = el.querySelector(`.icon.${direction}`);
    let cast = false;

    if (target.includes(id) === false) {
      cast = true;
      target.push(id);

      const idx = other.indexOf(id);

      if (idx > -1) {
        other.splice(idx, 1);
        render(() =>
          el.parentNode.parentNode.parentNode.querySelector(`.icon.vote.${otherDirection}`).classList.remove('voted'),
        );
      }

      render(() => icon.classList.add('voted'));
    } else {
      const idx = target.indexOf(id);

      if (idx > -1) {
        target.splice(idx, 1);
      }

      render(() => icon.classList.remove('voted'));
    }

    if (cast === true) {
      try {
        await request(voteUrl, {
          credentials: 'include',
          headers: {
            [headerKeys.auth]: sessionStorage.getItem(JWT),
            [headerKeys.accept]: headerValues.json,
            [headerKeys.ctype]: headerValues.json,
            [headerKeys.csrf]: await csrf(),
          },
          method: 'POST',
          body: JSON.stringify({
            id,
            direction,
            comment,
          }),
        });
      } catch (err) {
        void 0;
      }
    }

    await updateProfile('votes', direction === 'up' ? { down: other, up: target } : { down: target, up: other });

    adobeDataLayerTrack({
      event: 'assetInteraction',
      asset: {
        id: el.dataset.id || '',
        interactionType: direction === 'up' ? 'Thumbs Up' : 'Thumbs Down',
      },
    });
  }
}
